import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  TimeScaleOptions,
  Tooltip,
  TooltipCallbacks,
  ActiveElement,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import AnnotationPlugin, { AnnotationOptions } from 'chartjs-plugin-annotation'
import { hoursToMilliseconds } from 'date-fns'
import React, { forwardRef, useMemo, useRef } from 'react'
import { Line } from 'react-chartjs-2'
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types'
import { useTranslation } from 'react-i18next'

import { useCurrentUserLanguage } from '../../features/account/hooks/useCurrentUserLanguage'
import { ChartDataFormat } from '../../features/update-history/types/types'
import { formatDate } from '../../helpers/date'
import MouseOutCatcherPlugin, { MouseOutCatcherOptions } from '../../plugins/chartjs/MouseOutCatcherPlugin'
import VerticalTooltipLinePlugin, { VerticalTooltipLineOptions } from '../../plugins/chartjs/VerticalTooltipLinePlugin'
import utilsService from '../../services/UtilsService'

/**
 * Component which draws a chart with timeline on its X-axis and variable values on its Y-axes. y1 describes the left Y-axis and y2 the right Y-axis.
 */

ChartJS.register(TimeScale, LinearScale, CategoryScale, PointElement, LineElement, Tooltip, AnnotationPlugin, Filler)

export type ScaleConfig = {
  dataFormat: ChartDataFormat
  title?: string
  reverse?: boolean
  shorten?: boolean
  precision?: number
  stacked?: boolean
  maxY?: number
  minY?: number
}

export type TimeScaleConfig = Partial<TimeScaleOptions>

enum YAxisId {
  Y1 = 'y1',
  Y2 = 'y2',
}

enum XAxisId {
  X = 'x',
}

export type TimelineChartMark<T> = {
  position: number
  data: T
}

export type TimelineChartProps<VerticalMarkType> = {
  data?: ChartData<'line'>
  scaleConfig?: {
    [YAxisId.Y1]?: ScaleConfig
    [YAxisId.Y2]?: ScaleConfig
    [XAxisId.X]?: TimeScaleConfig
  }
  verticalMarks?: TimelineChartMark<VerticalMarkType>[]
  highlightedVerticalMarks?: TimelineChartMark<VerticalMarkType>[]
  selectedVerticalMarks?: TimelineChartMark<VerticalMarkType>[]
  highlightedPeriod?: { start: number; end: number }
  visibleDateRange?: { fromDate: Date; toDate: Date }
  onVerticalMarkClicked?: (data: VerticalMarkType) => void
  tooltipCallbacks?: Partial<TooltipCallbacks<'line'>>
  chartLineOptions?: ChartOptions<'line'>
  onChartClick?: (xValue: number | undefined, yElements: ActiveElement[]) => void
  onHover?: (event: any, chartElement: ActiveElement[]) => void
  onMouseOut?: (event: any) => void
  hoverPropagation?: boolean
  staticAnnotations?: AnnotationOptions[]
  onLayoutUpdate?: (info: TimelineChartLayoutInfo) => void
}

export type TimelineChartLayoutInfo = {
  chartAreaLeft: number
  chartAreaRight: number
  chartAreaBottom: number
  containerWidth: number
  xMin: number
  xMax: number
}

/**
 * Component representing the performance chart.
 *
 * NOTE: probably needs some layers of generalization as we go and figure out more use cases.
 */
export const TimelineChart = forwardRef<ChartJSOrUndefined<'line'>, TimelineChartProps<object>>(
  <VerticalMarkType extends object>(
    {
      data = { datasets: [] },
      scaleConfig,
      verticalMarks = [],
      highlightedVerticalMarks = [],
      selectedVerticalMarks = [],
      highlightedPeriod,
      visibleDateRange,
      onVerticalMarkClicked,
      tooltipCallbacks = {},
      chartLineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
      },
      onChartClick,
      hoverPropagation = false,
      onHover,
      onMouseOut,
      staticAnnotations = [],
      onLayoutUpdate,
    }: TimelineChartProps<VerticalMarkType>,
    ref?: React.ForwardedRef<ChartJSOrUndefined<'line'>>
  ) => {
    const { t } = useTranslation()
    const userLanguage = useCurrentUserLanguage()

    // Plugin-driven layout sync: Chart.js fires `afterUpdate` after every
    // layout / resize / data change has settled, so subscribing here gives
    // consumers fresh `chartArea`/`scales.x` values without polling via RAF
    // or ResizeObserver from React. The ref pattern keeps the plugin object
    // stable across renders while still calling the latest callback.
    const onLayoutUpdateRef = useRef(onLayoutUpdate)
    onLayoutUpdateRef.current = onLayoutUpdate
    // Last emitted layout, so the plugin only calls back when something actually
    // changed. This keeps the plugin self-defending: even a consumer that sets
    // state unconditionally from the callback can't drive an afterUpdate loop.
    const lastEmittedLayoutRef = useRef<TimelineChartLayoutInfo | null>(null)
    const layoutSyncPlugin = useMemo(
      () => ({
        id: 'gr-layout-sync',
        afterUpdate: (chart: ChartJS) => {
          const callback = onLayoutUpdateRef.current
          if (!callback || !chart.chartArea || !chart.canvas) return
          const next: TimelineChartLayoutInfo = {
            chartAreaLeft: chart.chartArea.left,
            chartAreaRight: chart.chartArea.right,
            chartAreaBottom: chart.chartArea.bottom,
            containerWidth: chart.canvas.offsetWidth,
            xMin: chart.scales.x.min as number,
            xMax: chart.scales.x.max as number,
          }
          const prev = lastEmittedLayoutRef.current
          if (
            prev &&
            prev.chartAreaLeft === next.chartAreaLeft &&
            prev.chartAreaRight === next.chartAreaRight &&
            prev.chartAreaBottom === next.chartAreaBottom &&
            prev.containerWidth === next.containerWidth &&
            prev.xMin === next.xMin &&
            prev.xMax === next.xMax
          ) {
            return
          }
          lastEmittedLayoutRef.current = next
          callback(next)
        },
      }),
      []
    )

    let timeDisplayFormats

    switch (userLanguage) {
      case 'ja':
      case 'zh':
        timeDisplayFormats = {
          day: 'yyyy年M月d日',
          month: 'yyyy年M月',
        }
        break

      case 'en':
        timeDisplayFormats = {
          day: 'MMM d, yyyy',
          week: 'LL / yyyy',
          month: 'MMM, yyyy',
        }
        break
    }

    // create annotation objects for vertical marks
    const verticalAnnotations: AnnotationOptions<'point'>[] = verticalMarks.map((mark) => ({
      type: 'point',
      xValue: mark.position,
      yValue: (ctx) => (scaleConfig?.y1?.reverse ? ctx.chart.scales['y1'].max : ctx.chart.scales['y1'].min),
      yScaleID: 'y1',
      backgroundColor: 'rgb(224, 224, 224)',
      borderColor: 'rgb(187, 187, 187)',
      borderWidth: 2,
      radius: 6,
      enter: (ctx) => {
        if (onVerticalMarkClicked) {
          ctx.chart.canvas.style.cursor = 'pointer'
          ctx.element.options.borderColor = 'rgb(189, 101, 207)'
          ctx.chart.draw()
        }
      },
      leave: (ctx) => {
        if (onVerticalMarkClicked) {
          ctx.element.options.borderColor = 'rgb(187, 187, 187)'
          ctx.chart.canvas.style.cursor = 'default'
          ctx.chart.draw()
        }
      },
      click: () => onVerticalMarkClicked && onVerticalMarkClicked(mark.data),
    }))

    // create annotation objects for highlighted vertical marks
    const highlightedVerticalAnnotations = highlightedVerticalMarks.flatMap(
      (mark) =>
        [
          {
            type: 'point',
            xValue: mark.position,
            yValue: (ctx) => (scaleConfig?.y1?.reverse ? ctx.chart.scales['y1'].max : ctx.chart.scales['y1'].min),
            yScaleID: 'y1',
            backgroundColor: 'rgb(224, 224, 224)',
            borderColor: 'rgb(189, 101, 207)',
            borderWidth: 2,
            radius: 6,
            enter: (ctx) => {
              if (onVerticalMarkClicked) {
                ctx.chart.canvas.style.cursor = 'pointer'
                ctx.chart.draw()
              }
            },
            leave: (ctx) => {
              if (onVerticalMarkClicked) {
                ctx.chart.canvas.style.cursor = 'default'
                ctx.chart.draw()
              }
            },
            click: () => onVerticalMarkClicked && onVerticalMarkClicked(mark.data),
          },
        ] as AnnotationOptions[]
    )

    // create annotation objects for selected vertical marks
    const selectedVerticalAnnotations = selectedVerticalMarks.flatMap((mark) => {
      return [
        {
          type: 'line',
          scaleID: 'x',
          yScaleID: 'y1',
          value: mark.position,
          borderColor: 'rgb(150, 150, 150)',
          borderDash: [5, 5],
          borderWidth: 2,
        },
        {
          type: 'point',
          xValue: mark.position,
          yValue: (ctx) => (scaleConfig?.y1?.reverse ? ctx.chart.scales['y1'].max : ctx.chart.scales['y1'].min),
          yScaleID: 'y1',
          backgroundColor: 'rgb(224, 224, 224)',
          borderColor: 'rgb(189, 101, 207)',
          borderWidth: 2,
          radius: 6,
          enter: (ctx) => {
            if (onVerticalMarkClicked) {
              ctx.chart.canvas.style.cursor = 'pointer'
              ctx.chart.draw()
            }
          },
          leave: (ctx) => {
            if (onVerticalMarkClicked) {
              ctx.chart.canvas.style.cursor = 'default'
              ctx.chart.draw()
            }
          },
          click: () => onVerticalMarkClicked && onVerticalMarkClicked(mark.data),
        },
      ] as AnnotationOptions[]
    })

    // Create annotations for event duration
    const highlightedPeriodAnnotations: AnnotationOptions[] = []
    if (highlightedPeriod) {
      // Only show highlighted period if it overlaps with the visible date range
      const shouldShowHighlightedPeriod =
        !visibleDateRange || (highlightedPeriod.start <= visibleDateRange.toDate.getTime() && highlightedPeriod.end >= visibleDateRange.fromDate.getTime())

      if (!shouldShowHighlightedPeriod) {
        // Skip rendering if period is outside visible range
      } else {
        // Calculate if it's a single day event using the original timestamps
        const isSingleDay = highlightedPeriod.end - highlightedPeriod.start <= hoursToMilliseconds(24)

        // Fix timezone issue: the timestamp is in UTC, but Chart.js renders in local timezone
        const endDate = new Date(highlightedPeriod.end)
        const normalizedEnd = new Date(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate() - 1, 23, 59, 59, 999).getTime()

        const eventBorderColor = 'rgba(189, 101, 207, 0.8)'

        // Cap the end time to current time if event extends into the future
        // Normalize current time to end of today (so gray area extends through all of today)
        const now = new Date()
        const normalizedCurrentTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime()
        const cappedEndTime = Math.min(normalizedEnd, normalizedCurrentTime)

        // Clip to visible date range if provided
        const visibleStart = visibleDateRange?.fromDate.getTime()
        let visibleEnd = visibleDateRange?.toDate.getTime()

        // Normalize visibleEnd to fix timezone issue (same as event end)
        if (visibleEnd) {
          const visibleEndDate = new Date(visibleEnd)
          visibleEnd = new Date(visibleEndDate.getUTCFullYear(), visibleEndDate.getUTCMonth(), visibleEndDate.getUTCDate() - 1, 23, 59, 59, 999).getTime()
        }

        if (isSingleDay) {
          // For single-day events
          highlightedPeriodAnnotations.push({
            type: 'line',
            xMin: highlightedPeriod.start,
            xMax: highlightedPeriod.start,
            borderColor: eventBorderColor,
            borderWidth: 2,
            borderDash: [5, 5],
          })
        } else {
          // For multi-day events show the shaded area if we have data
          if (cappedEndTime > highlightedPeriod.start) {
            // Clip the shaded area to the visible range
            const boxStart = visibleStart ? Math.max(highlightedPeriod.start, visibleStart) : highlightedPeriod.start
            const boxEnd = visibleEnd ? Math.min(cappedEndTime, visibleEnd) : cappedEndTime

            highlightedPeriodAnnotations.push({
              type: 'box',
              xMin: boxStart,
              xMax: boxEnd,
              backgroundColor: 'rgba(200, 200, 200, 0.2)',
              borderWidth: 0,
            })

            // Only show start line if it's within visible range
            const showStartLine = !visibleStart || highlightedPeriod.start >= visibleStart
            if (showStartLine) {
              highlightedPeriodAnnotations.push({
                type: 'line',
                xMin: highlightedPeriod.start,
                xMax: highlightedPeriod.start,
                borderColor: eventBorderColor,
                borderWidth: 2,
                borderDash: [5, 5],
              })
            }

            // Only show end line if event has actually ended (not capped by current time) and it's within visible range
            // Use strict < to ensure event has truly ended (not just equal to today's end)
            const eventHasEnded = normalizedEnd < normalizedCurrentTime
            const showEndLine = eventHasEnded && (!visibleEnd || normalizedEnd <= visibleEnd)
            if (showEndLine) {
              highlightedPeriodAnnotations.push({
                type: 'line',
                xMin: normalizedEnd,
                xMax: normalizedEnd,
                borderColor: eventBorderColor,
                borderWidth: 2,
                borderDash: [5, 5],
              })
            }
          }
        }
      }
    }

    const options: ChartOptions<'line'> & { plugins: { verticalTooltipLine?: VerticalTooltipLineOptions; mouseOutCatcher: MouseOutCatcherOptions } } = {
      // Debounce ResizeObserver callbacks to prevent infinite shrink/expand loop
      // at fractional browser zoom levels (e.g. 90% in Chromium on Windows) when
      // `responsive: true` is combined with `maintainAspectRatio: true`. (GR-2925)
      resizeDelay: 200,
      ...chartLineOptions,
      onClick: (event, elements, chart) => {
        const x = elements?.[0]?.element.x || chart.tooltip?.caretX
        const xValue = x && chart.scales.x.getValueForPixel(x)
        onChartClick?.(xValue, elements)
      },
      onHover: (event: any, chartElement) => {
        if (event && onChartClick) {
          event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default'
        }

        if (event && hoverPropagation && onHover) {
          onHover(event, chartElement)
        }
      },
      scales: {
        x: {
          type: 'time' as const,
          time: {
            unit: 'day' as const,
            displayFormats: timeDisplayFormats,
            isoWeekday: true,
          },
          title: {
            display: true,
            align: 'center',
            text: t('common:date'),
            padding: { top: 16 },
          },
          ticks: {
            autoSkip: true,
            autoSkipPadding: 16,
          },
          ...scaleConfig?.[XAxisId.X],
        },
        y1: {
          position: 'left' as const,
          beginAtZero: true,
          display: !!data?.datasets.find((dataset) => dataset.yAxisID === YAxisId.Y1),
          title: {
            text: scaleConfig?.y1?.title,
            display: !!scaleConfig?.y1?.title,
            padding: { bottom: 16 },
          },
          ticks: {
            precision: scaleConfig?.y1?.precision || 0,
            callback: (value, index, ticks) => {
              switch (scaleConfig?.y1?.dataFormat) {
                case ChartDataFormat.Currency:
                  return utilsService.formatCurrency(value as number, { shorten: scaleConfig?.y1?.shorten, mantissa: 2 })
                case ChartDataFormat.Percentage:
                  return utilsService.formatNumber(value as number, { mantissa: 0 }) + '%'
                default:
                  return utilsService.formatNumber(value as number, { shorten: scaleConfig?.y1?.shorten, mantissa: 2 })
              }
            },
          },
          reverse: scaleConfig?.y1?.reverse,
          stacked: scaleConfig?.y1?.stacked,
          suggestedMax: scaleConfig?.y1?.maxY,
          suggestedMin: scaleConfig?.y1?.minY,
        },
        y2: {
          position: 'right' as const,
          beginAtZero: true,
          display: !!data?.datasets.find((dataset) => dataset.yAxisID === YAxisId.Y2),
          title: {
            text: scaleConfig?.y2?.title,
            display: !!scaleConfig?.y2?.title,
            padding: { bottom: 16 },
          },
          ticks: {
            precision: scaleConfig?.y2?.precision || 0,
            callback: (value, index, ticks) => {
              switch (scaleConfig?.y2?.dataFormat) {
                case ChartDataFormat.Currency:
                  return utilsService.formatCurrency(value as number, { shorten: scaleConfig?.y2?.shorten, mantissa: 2 })
                case ChartDataFormat.Percentage:
                  return utilsService.formatNumber(value as number, { mantissa: 0 }) + '%'
                default:
                  return utilsService.formatNumber(value as number, { shorten: scaleConfig?.y2?.shorten, mantissa: 2 })
              }
            },
          },
          suggestedMax: scaleConfig?.y2?.maxY,
          suggestedMin: scaleConfig?.y2?.minY,
          reverse: scaleConfig?.y2?.reverse,
          stacked: scaleConfig?.y2?.stacked,
        },
      },
      elements: {
        point: {
          radius: 0,
        },
        line: {
          borderWidth: 2,
        },
      },
      interaction: {
        intersect: false,
        axis: 'x',
      },
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            pointStyle: 'circle' as const,
            usePointStyle: true,
            padding: 16,
          },
        },
        tooltip: {
          position: 'average',
          yAlign: 'center',
          titleFont: {
            size: 14,
            weight: 'bolder' as const,
          },
          titleMarginBottom: 14,
          bodySpacing: 6,
          boxPadding: 6,
          usePointStyle: true,
          padding: 10,
          itemSort: (a, b) => {
            return a.element.y - b.element.y
          },
          callbacks: {
            title: (tooltipItems) => {
              return formatDate(tooltipItems[0].parsed.x, { language: userLanguage })
            },
            label: (tooltipItem) => {
              const yAxisID = tooltipItem.dataset.yAxisID as YAxisId
              const yAxisDataFormat = scaleConfig?.[yAxisID]?.dataFormat

              switch (yAxisDataFormat) {
                case ChartDataFormat.Currency:
                  return ` ${tooltipItem.dataset.label}: $${tooltipItem.formattedValue}`
                case ChartDataFormat.Percentage:
                  return ` ${tooltipItem.dataset.label}: ${tooltipItem.parsed.y.toFixed(1)}%`
                default:
                  return ` ${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`
              }
            },
            ...tooltipCallbacks,
          },
        },
        verticalTooltipLine: {
          color: '0, 0, 0',
          lineWidth: 2,
          lineDash: [5, 5],
        },
        annotation: {
          clip: false,
          annotations: [
            ...verticalAnnotations,
            ...highlightedVerticalAnnotations,
            ...selectedVerticalAnnotations,
            ...highlightedPeriodAnnotations,
            ...staticAnnotations,
          ],
        },
        mouseOutCatcher: {
          onMouseOut: (event) => {
            if (onMouseOut) {
              onMouseOut(event)
            }
          },
        },
      },
    }

    return <Line ref={ref} data={data} options={options} plugins={[new VerticalTooltipLinePlugin(), new MouseOutCatcherPlugin(), layoutSyncPlugin]} />
  }
)
